/**
 * Своя учётная запись: вход в два шага и выход.
 *
 * Настраивает это каждый сам за себя, а не администратор: ключ надо внести в
 * приложение на своём телефоне, и посреднику там делать нечего. Поэтому окно
 * доступно всем, а не только тем, кто видит управление.
 *
 * Ключ сохраняется только после того, как человек ввёл по нему верный код.
 * Иначе неверно внесённый ключ запер бы его снаружи: пароль подходит, а код не
 * сходится никогда, и снять это смог бы только администратор.
 */

import { useState } from 'react'

import { ApiError, api, type Identity } from './api'
import { dict, type Lang } from './i18n'
import { THEMES, type Theme } from './theme'

type Props = {
  identity: Identity
  lang: Lang
  /** Тема оформления и её смена — состоянием владеет приложение. */
  theme: Theme
  onTheme: (theme: Theme) => void
  onChanged: (identity: Identity) => void
  onSignOut: () => void
  onClose: () => void
}

/** Ключ читается людьми и переписывается руками — разбиваем на четвёрки. */
function readable(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

export function Profile({ identity, lang, theme, onTheme, onChanged, onSignOut, onClose }: Props) {
  const t = dict(lang)
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  function complain(caught: unknown) {
    setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
  }

  async function begin() {
    setBusy(true)
    setError('')
    try {
      setSetup(await api.startTwoStep())
    } catch (caught) {
      complain(caught)
    } finally {
      setBusy(false)
    }
  }

  async function confirm() {
    if (!setup) return
    setBusy(true)
    setError('')
    try {
      await api.confirmTwoStep(setup.secret, code)
      setSetup(null)
      setCode('')
      onChanged({ ...identity, twoStep: true })
    } catch (caught) {
      complain(caught)
    } finally {
      setBusy(false)
    }
  }

  async function stop() {
    setBusy(true)
    setError('')
    try {
      await api.stopTwoStep()
      onChanged({ ...identity, twoStep: false })
    } catch (caught) {
      complain(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="confirm" role="dialog" aria-modal="true">
      <div className="cbox">
        <h4>{identity.name}</h4>

        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}

        <h5 className="c-sub">{t.theme}</h5>
        {/* Тема — личный выбор, как язык: живёт в браузере, а не в учётной
            записи, и меняется без сохранения на сервере. */}
        <div className="chips theme-picker" role="radiogroup" aria-label={t.theme}>
          {THEMES.map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={theme === option}
              aria-pressed={theme === option}
              onClick={() => onTheme(option)}
            >
              {option === 'light' ? t.themeLight : option === 'dark' ? t.themeDark : t.themeSystem}
            </button>
          ))}
        </div>

        <h5 className="c-sub">{t.twoStep}</h5>

        {identity.twoStep ? (
          <>
            <p className="hint">{t.twoStepOn}</p>
            <button className="act ghost" disabled={busy} onClick={() => void stop()}>
              {t.twoStepOff}
            </button>
          </>
        ) : setup ? (
          <>
            <p className="hint">{t.twoStepHint}</p>
            <div className="kv">
              <span>{t.twoStepKey}</span>
              <b className="secret">{readable(setup.secret)}</b>
            </div>
            {/* На телефоне эта ссылка открывает аутентификатор и вносит ключ
                сама — переписывать его руками приходится только на компьютере. */}
            <a className="act ghost" href={setup.uri}>
              {t.twoStepOpenApp}
            </a>
            <label className="field">
              <span>{t.twoStepCode}</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123 456"
              />
            </label>
            <button className="act" disabled={busy} onClick={() => void confirm()}>
              {busy ? t.working : t.confirmAction}
            </button>
          </>
        ) : (
          <button className="act ghost" disabled={busy} onClick={() => void begin()}>
            {t.twoStep}
          </button>
        )}

        <button className="act" onClick={onSignOut}>
          {t.signOut}
        </button>
        <button className="act ghost" onClick={onClose}>
          {t.confirmNo}
        </button>
      </div>
    </div>
  )
}
